import React, { useEffect, useState } from 'react';
import { ShieldAlert, ShieldCheck, CheckCircle2, XCircle, AlertCircle, CalendarClock, ClipboardList, FileText } from 'lucide-react';
import { api, DEMO } from '../lib/api';
import { hasCap } from '../lib/capabilities';
import SendToClinician from './SendToClinician';
import DiagnosisClassBadge from './DiagnosisClassBadge';
import './SectionPhpEligibility.css';

const STATUS = {
  ELIGIBLE: { label: 'Eligible', tone: 'pass' },
  NOT_ELIGIBLE: { label: 'Not eligible', tone: 'fail' },
  PENDING_INFORMATION: { label: 'Pending information', tone: 'pending' },
  PENDING_PHYSICIAN_CERTIFICATION: { label: 'Pending physician certification', tone: 'pending' },
  REQUIRES_HIGHER_LEVEL_OF_CARE: { label: 'Requires higher level of care', tone: 'safety' },
};
const STATE_ICON = { PASS: CheckCircle2, FAIL: XCircle, PENDING: AlertCircle };
const PHQ9_SEVERITY_LABEL = { minimal: 'Minimal depression', mild: 'Mild depression', moderate: 'Moderate depression', 'moderately severe': 'Moderately severe depression', severe: 'Severe depression' };

const RELATIONSHIP_OPTIONS = [
  ['', '— Unset (missing → PENDING) —'],
  ['STEP_DOWN_FROM_INPATIENT', 'Step-down from inpatient care'],
  ['AT_RISK_OF_INPATIENT', 'At risk of inpatient admission without PHP'],
  ['NEITHER', 'Neither (does not meet level-of-care requirement)'],
];

function emptyForm() {
  return {
    program_type: 'php',
    acute_condition_supported: '', functional_impairment_supported: '', php_relationship_to_inpatient: '',
    less_intensive_care_insufficient: '', prescribed_php_hours_per_week: '', clinical_need_for_20_hours: '',
    patient_can_tolerate_20_hours: '', participation_capacity: '', active_treatment_supported: '', higher_level_of_care_required: '',
  };
}

function emptyCertForm() {
  return {
    type: 'initial', under_physician_care: false, plan_of_care_established: false,
    min_20_hours_medically_necessary: false, inpatient_criterion_established: false,
    physician_name: '', npi: '', response_to_treatment: '', continuing_inpatient_risk: '', updated_goals: '', discharge_planning: '',
  };
}

function emptyAuthForm() {
  return { id: null, program_type: 'php', payer: '', auth_number: '', status: 'pending', effective_date: '', expiry_date: '', notes: '' };
}

function emptyEscalationForm() {
  return { priority: 'high', reason: '', notes: '' };
}

function demoPayload(patient) {
  return {
    assessments: [], enrollments: [], certifications: [], escalations: [], prior_authorizations: [],
    certification_complete: false,
    recertification: { next_due_date: null, overdue: false, upcoming: false },
    supported_diagnoses_configured: (patient.diagnoses || []).length > 0,
  };
}

/** Compact demo mirror of the server's deterministic engine (illustrative only). */
function demoEvaluate(body, patient, payload) {
  const checks = {};
  const activeDx = (patient.diagnoses || []).filter(d => ['active', 'chronic'].includes(d.status));
  const diagnosisSupported = activeDx.length ? true : false;
  const diagnosisCode = activeDx.length ? activeDx[0].code : null;
  checks.diagnosis = diagnosisSupported
    ? { label: 'Qualifying diagnosis (A57053 Group 1)', state: 'PASS', detail: 'ICD-10 ' + diagnosisCode }
    : { label: 'Qualifying diagnosis (A57053 Group 1)', state: 'FAIL', detail: 'No qualifying/supported PHP diagnosis identified.' };

  const boolCriteria = {
    acute_condition_supported: ['Acute psychiatric condition', 'Documentation does not support an acute psychiatric condition requiring PHP-level intensity.'],
    functional_impairment_supported: ['Severe functional impairment', 'Severe functional impairment requiring PHP is not established.'],
    less_intensive_care_insufficient: ['Less-intensive care insufficient', "Patient's needs can be addressed at a less intensive level of care."],
    clinical_need_for_20_hours: ['20 hrs/week clinical need', 'Minimum PHP intensity (20 hours/week) is not clinically supported.'],
    patient_can_tolerate_20_hours: ['Can tolerate 20 hrs/week', 'Patient cannot tolerate the minimum 20 hours/week intensity.'],
    participation_capacity: ['Capacity to participate', 'Patient cannot currently participate in the program at the required intensity.'],
    active_treatment_supported: ['Active treatment plan', 'Active treatment plan / ongoing psychiatric treatment requirement is not met.'],
  };
  for (const [k, [label, failReason]] of Object.entries(boolCriteria)) {
    const v = body[k];
    checks[k] = v === true ? { label, state: 'PASS' } : v === false ? { label, state: 'FAIL', detail: failReason } : { label, state: 'PENDING', detail: 'Information required.' };
  }
  const rel = body.php_relationship_to_inpatient;
  checks.inpatient_relationship = rel === 'STEP_DOWN_FROM_INPATIENT' ? { label: 'Inpatient / step-down relationship', state: 'PASS', detail: 'Step-down from inpatient care.' }
    : rel === 'AT_RISK_OF_INPATIENT' ? { label: 'Inpatient / step-down relationship', state: 'PASS', detail: 'At risk of inpatient admission without PHP.' }
      : rel === 'NEITHER' ? { label: 'Inpatient / step-down relationship', state: 'FAIL', detail: "Patient does not meet Medicare's inpatient/PHP level-of-care relationship requirement." }
        : { label: 'Inpatient / step-down relationship', state: 'PENDING', detail: 'Information required.' };
  const hours = body.prescribed_php_hours_per_week;
  checks.hours_prescribed = hours == null ? { label: 'Prescribed hours/week', state: 'PENDING', detail: 'Information required.' }
    : hours < 20 ? { label: 'Prescribed hours/week', state: 'FAIL', detail: 'Minimum PHP intensity (20 hours/week) is not met (' + hours + ' hrs/week).' }
      : { label: 'Prescribed hours/week', state: 'PASS', detail: hours + ' hrs/week prescribed.' };

  let status;
  if (body.higher_level_of_care_required === true) status = 'REQUIRES_HIGHER_LEVEL_OF_CARE';
  else {
    const fails = Object.values(checks).filter(c => c.state === 'FAIL');
    const pending = Object.values(checks).filter(c => c.state === 'PENDING');
    if (fails.length) status = 'NOT_ELIGIBLE';
    else if (pending.length) status = 'PENDING_INFORMATION';
    else if (!payload.certification_complete) status = 'PENDING_PHYSICIAN_CERTIFICATION';
    else status = 'ELIGIBLE';
  }
  const reasons = [];
  Object.values(checks).forEach(c => { if (c.state === 'FAIL') reasons.push(c.detail || c.label); });
  Object.values(checks).forEach(c => { if (c.state === 'PENDING') reasons.push(c.detail || c.label); });
  return { status, checks, reasons, diagnosis_supported: diagnosisSupported, diagnosis_code: diagnosisCode, certification_complete: payload.certification_complete };
}

export default function SectionPhpEligibility({ patient, user, notify, logDemo, navigate, openAssessments }) {
  const canManage = hasCap(user, 'eligibility.manage');
  const [payload, setPayload] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [certForm, setCertForm] = useState(emptyCertForm());
  const [authForm, setAuthForm] = useState(emptyAuthForm());
  const [escForm, setEscForm] = useState(emptyEscalationForm());

  async function load() {
    try {
      if (DEMO) { setPayload(demoPayload(patient)); return; }
      setPayload(await api(`/patients/${patient.id}/php`));
    } catch (e) { notify(e.message); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [patient.id]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const setCert = k => e => setCertForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  async function evaluate(e) {
    e.preventDefault(); setBusy(true);
    try {
      const body = { program_type: form.program_type };
      ['acute_condition_supported', 'functional_impairment_supported', 'less_intensive_care_insufficient', 'clinical_need_for_20_hours', 'patient_can_tolerate_20_hours', 'participation_capacity', 'active_treatment_supported', 'higher_level_of_care_required'].forEach(k => { body[k] = form[k] === '' ? null : form[k] === 'true'; });
      body.php_relationship_to_inpatient = form.php_relationship_to_inpatient === '' ? null : form.php_relationship_to_inpatient;
      body.prescribed_php_hours_per_week = form.prescribed_php_hours_per_week === '' ? null : parseInt(form.prescribed_php_hours_per_week, 10);
      if (DEMO) {
        const r = demoEvaluate(body, patient, payload);
        setResult(r);
        logDemo('php.eligibility_evaluated', { patient_id: patient.id, status: r.status });
        notify(`Eligibility decision: ${r.status}.`);
      } else {
        const r = await api(`/patients/${patient.id}/php/eligibility`, { method: 'POST', body });
        setResult(r);
        await load();
        notify(`Eligibility decision: ${r.status}.`);
      }
    } catch (e2) { notify(e2.message); } finally { setBusy(false); }
  }

  async function certify(e) {
    e.preventDefault(); setBusy(true);
    try {
      if (DEMO) {
        setPayload(p => ({
          ...p,
          certification_complete: certForm.type === 'initial' && certForm.under_physician_care && certForm.plan_of_care_established && certForm.min_20_hours_medically_necessary && certForm.inpatient_criterion_established,
          certifications: [...(p.certifications || []), { id: 'demo-cert-' + Date.now(), type: certForm.type, physician_name: certForm.physician_name, npi: certForm.npi, signed_at: new Date().toISOString(), under_physician_care: certForm.under_physician_care, plan_of_care_established: certForm.plan_of_care_established, min_20_hours_medically_necessary: certForm.min_20_hours_medically_necessary, inpatient_criterion_established: certForm.inpatient_criterion_established }],
        }));
        logDemo('php.certified', { patient_id: patient.id, kind: certForm.type });
        notify('Certification recorded (demo).');
      } else {
        await api(`/patients/${patient.id}/php/certification`, { method: 'POST', body: certForm });
        await load();
        notify('Certification recorded.');
      }
      setCertForm(emptyCertForm());
    } catch (e2) { notify(e2.message); } finally { setBusy(false); }
  }

  const setAuth = k => e => setAuthForm(f => ({ ...f, [k]: e.target.value }));
  const setEsc = k => e => setEscForm(f => ({ ...f, [k]: e.target.value }));

  async function saveAuth(e) {
    e.preventDefault(); setBusy(true);
    try {
      if (DEMO) {
        const existing = authForm.id ? (payload.prior_authorizations || []).find(a => String(a.id) === String(authForm.id)) : null;
        const row = { ...(existing || {}), ...authForm, id: authForm.id || ('demo-auth-' + Date.now()), created_at: existing?.created_at || new Date().toISOString() };
        setPayload(p => ({ ...p, prior_authorizations: [row, ...(p.prior_authorizations || []).filter(a => String(a.id) !== String(row.id))] }));
        logDemo('php.prior_authorization_saved', { patient_id: patient.id, status: authForm.status });
        notify('Prior authorization saved (demo).');
      } else {
        await api(`/patients/${patient.id}/php/prior-authorizations`, { method: 'POST', body: authForm });
        await load();
        notify('Prior authorization saved.');
      }
      setAuthForm(emptyAuthForm());
    } catch (e2) { notify(e2.message); } finally { setBusy(false); }
  }

  async function createEscalation(e) {
    e.preventDefault(); setBusy(true);
    try {
      if (DEMO) {
        setPayload(p => ({ ...p, escalations: [{ id: 'demo-esc-' + Date.now(), status: 'open', priority: escForm.priority, reason: escForm.reason, notes: escForm.notes, created_at: new Date().toISOString() }, ...(p.escalations || [])] }));
        logDemo('php.escalation_created', { patient_id: patient.id });
        notify('Escalation opened (demo).');
      } else {
        await api(`/patients/${patient.id}/php/escalations`, { method: 'POST', body: escForm });
        await load();
        notify('Escalation opened.');
      }
      setEscForm(emptyEscalationForm());
    } catch (e2) { notify(e2.message); } finally { setBusy(false); }
  }

  async function resolveEscalation(escId, status) {
    setBusy(true);
    try {
      if (DEMO) {
        setPayload(p => ({ ...p, escalations: (p.escalations || []).map(es => String(es.id) === String(escId) ? { ...es, status, notes: (es.notes ? es.notes + '\n' : '') + '[' + status + ' by ' + user.name + ']' } : es) }));
        logDemo('php.escalation_' + status, { escalation_id: escId });
        notify('Escalation ' + status + ' (demo).');
      } else {
        await api(`/php/escalations/${escId}`, { method: 'POST', body: { status } });
        await load();
        notify('Escalation ' + status + '.');
      }
    } catch (e2) { notify(e2.message); } finally { setBusy(false); }
  }

  const latest = payload?.assessments?.[0] || null;
  const recert = payload?.recertification || { next_due_date: null, overdue: false, upcoming: false };

  return (
    <>
      {result && result.status === 'REQUIRES_HIGHER_LEVEL_OF_CARE' && (
        <section className="content-card php-safety-alert" role="alert">
          <ShieldAlert size={22} />
          <div>
            <strong>Higher level of care indicated</strong>
            <p>The patient may require immediate clinical evaluation for a higher level of care (e.g. inpatient psychiatric admission). This determination takes precedence over every other criterion. Escalate for clinical review now.</p>
          </div>
        </section>
      )}

      <section className="content-card">
        <div className="content-heading">
          <div>
            <div className="eyebrow">MEDICARE PHP / IOP</div>
            <h3>Partial hospitalization eligibility</h3>
            <p>Multi-criterion clinical determination. An ICD-10 diagnosis is one input and can never, alone, establish eligibility. Missing information is never treated as a pass.</p>
          </div>
          <SendToClinician user={user} notify={notify} navigate={navigate} patientId={patient.id} requestType="php_eligibility" relatedEntityId={latest?.id} label="Send to clinician" />
        </div>

        <div className="php-summary">
          <div className="php-status">
            <span className={`php-status-badge ${STATUS[latest?.status]?.tone || 'pending'}`}>
              {latest ? STATUS[latest.status]?.label || latest.status : 'No decision yet'}
            </span>
            <small>Latest decision · {latest?.program_type === 'iop' ? 'IOP' : 'PHP'} · rule {latest?.rule_version || 'php-v1'}</small>
          </div>
          {recert.next_due_date && (
            <div className={`php-recert ${recert.overdue ? 'overdue' : recert.upcoming ? 'upcoming' : ''}`}>
              <CalendarClock size={15} />
              <span>Recertification due {recert.next_due_date}{recert.overdue ? ' (overdue)' : recert.upcoming ? ' (upcoming)' : ''}</span>
            </div>
          )}
        </div>

        {result && <ResultPanel result={result} patient={patient} />}

        <ClinicalScreenings patient={patient} notify={notify} onViewAll={openAssessments} />

        {canManage && (
          <div className="php-form">
            <div className="content-heading"><div><h4>New eligibility evaluation</h4><p>Leave a field unset to record it as missing (PENDING). No field defaults to “yes”.</p></div></div>
            <form onSubmit={evaluate}>
              <div className="form-grid">
                <label>Program<select value={form.program_type} onChange={set('program_type')}><option value="php">PHP</option><option value="iop">IOP</option></select></label>
                <TriSelect label="Acute psychiatric condition" value={form.acute_condition_supported} onChange={set('acute_condition_supported')} />
                <TriSelect label="Severe functional impairment" value={form.functional_impairment_supported} onChange={set('functional_impairment_supported')} />
                <label>Inpatient / step-down relationship<select value={form.php_relationship_to_inpatient} onChange={set('php_relationship_to_inpatient')}>{RELATIONSHIP_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
                <TriSelect label="Less-intensive care insufficient" value={form.less_intensive_care_insufficient} onChange={set('less_intensive_care_insufficient')} />
                <label>Prescribed hours/week<input type="number" min="0" max="168" value={form.prescribed_php_hours_per_week} onChange={set('prescribed_php_hours_per_week')} placeholder="e.g. 20"/></label>
                <TriSelect label="Clinical need for 20 hrs/week" value={form.clinical_need_for_20_hours} onChange={set('clinical_need_for_20_hours')} />
                <TriSelect label="Can tolerate 20 hrs/week" value={form.patient_can_tolerate_20_hours} onChange={set('patient_can_tolerate_20_hours')} />
                <TriSelect label="Capacity to participate" value={form.participation_capacity} onChange={set('participation_capacity')} />
                <TriSelect label="Active treatment plan" value={form.active_treatment_supported} onChange={set('active_treatment_supported')} />
                <TriSelect label="Higher level of care required (safety)" value={form.higher_level_of_care_required} onChange={set('higher_level_of_care_required')} />
              </div>
              <button className="button primary" disabled={busy}>{busy ? 'Evaluating…' : 'Run eligibility evaluation'}</button>
            </form>
          </div>
        )}

        {canManage && (
          <div className="php-form section-space">
            <div className="content-heading"><div><h4>Physician certification</h4><p>Initial certification and recertification. The signing physician is your authenticated account.</p></div><SendToClinician user={user} notify={notify} navigate={navigate} patientId={patient.id} requestType="physician_certification" relatedEntityId={payload?.certifications?.[0]?.id} label="Send certification request" /></div>
            <p className="php-signer">E-signed as <strong>{user.name || '—'}</strong>{user.title ? `, ${user.title}` : ''}{user.npi ? ` · NPI ${user.npi}` : ''} — an immutable PDF snapshot (SHA-256 + IP) is stored with each certification.</p>
            <form onSubmit={certify}>
              <div className="form-grid">
                <label>Type<select value={certForm.type} onChange={setCert('type')}><option value="initial">Initial certification</option><option value="recertification">Recertification</option></select></label>
                <label>Physician name<input value={certForm.physician_name} onChange={setCert('physician_name')} maxLength={200} placeholder="Full name"/></label>
                <label>NPI<input value={certForm.npi} onChange={setCert('npi')} maxLength={10} inputMode="numeric" placeholder="10-digit NPI"/></label>
              </div>
              <div className="php-attestations">
                <label className="check-row"><input type="checkbox" checked={certForm.under_physician_care} onChange={setCert('under_physician_care')} /><span>Patient is under the care of a physician.</span></label>
                <label className="check-row"><input type="checkbox" checked={certForm.plan_of_care_established} onChange={setCert('plan_of_care_established')} /><span>A plan of care has been established and is periodically reviewed.</span></label>
                <label className="check-row"><input type="checkbox" checked={certForm.min_20_hours_medically_necessary} onChange={setCert('min_20_hours_medically_necessary')} /><span>At least 20 hours/week is medically necessary.</span></label>
                <label className="check-row"><input type="checkbox" checked={certForm.inpatient_criterion_established} onChange={setCert('inpatient_criterion_established')} /><span>The patient would otherwise require inpatient psychiatric care.</span></label>
              </div>
              {certForm.type === 'recertification' && (
                <div className="form-grid">
                  <label className="span2">Response to treatment<textarea rows={2} value={certForm.response_to_treatment} onChange={setCert('response_to_treatment')} maxLength={5000}/></label>
                  <label className="span2">Continuing inpatient risk<textarea rows={2} value={certForm.continuing_inpatient_risk} onChange={setCert('continuing_inpatient_risk')} maxLength={5000}/></label>
                </div>
              )}
              <button className="button primary" disabled={busy}>{busy ? 'Recording…' : 'Record certification'}</button>
            </form>
          </div>
        )}

        <section className="section-space">
          <div className="content-heading"><div><h4>Safety escalations</h4><p>Opened automatically when a higher level of care is indicated, or manually. Acknowledge and resolve to close the loop.</p></div></div>
          {(payload?.escalations || []).length === 0 && <p className="empty-copy">No escalations.</p>}
          {(payload?.escalations || []).map(es => (
            <div key={es.id} className="php-escalation">
              <div className="php-escalation-head">
                <span className={`badge ${es.status === 'open' ? 'badge-danger' : es.status === 'acknowledged' ? 'badge-warn' : 'badge-ok'}`}>{es.status}</span>
                <span className="badge neutral">{es.priority}</span>
                <small>{es.created_at}</small>
              </div>
              {es.reason && <p className="php-escalation-reason">{es.reason}</p>}
              {es.notes && <pre className="php-escalation-notes">{es.notes}</pre>}
              {canManage && es.status !== 'resolved' && (
                <div className="php-escalation-actions">
                  {es.status === 'open' && <button type="button" className="button ghost" disabled={busy} onClick={() => resolveEscalation(es.id, 'acknowledged')}>Acknowledge</button>}
                  <button type="button" className="button ghost" disabled={busy} onClick={() => resolveEscalation(es.id, 'resolved')}>Resolve</button>
                </div>
              )}
            </div>
          ))}
          {canManage && (
            <form className="php-escalation-form" onSubmit={createEscalation}>
              <div className="form-grid">
                <label>Priority<select value={escForm.priority} onChange={setEsc('priority')}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option></select></label>
                <label className="span2">Reason<textarea rows={2} value={escForm.reason} onChange={setEsc('reason')} maxLength={2000} placeholder="Why this patient needs escalation"/></label>
                <label className="span2">Notes<textarea rows={2} value={escForm.notes} onChange={setEsc('notes')} maxLength={5000}/></label>
              </div>
              <button className="button secondary" disabled={busy}>Open escalation</button>
            </form>
          )}
        </section>

        <section className="section-space">
          <div className="content-heading"><div><h4>Prior authorization</h4><p>Tracking only — payer authorization never gates or changes eligibility.</p></div></div>
          {(payload?.prior_authorizations || []).length === 0 && <p className="empty-copy">No prior authorizations recorded.</p>}
          {(payload?.prior_authorizations || []).length > 0 && (
            <div className="table-scroll"><table>
              <thead><tr><th>Payer</th><th>Auth #</th><th>Program</th><th>Status</th><th>Effective</th><th>Expiry</th>{canManage && <th></th>}</tr></thead>
              <tbody>{(payload.prior_authorizations || []).map(a => (
                <tr key={a.id}>
                  <td>{a.payer || '—'}</td>
                  <td>{a.auth_number || '—'}</td>
                  <td><span className="badge neutral">{a.program_type}</span></td>
                  <td><span className={`badge ${a.status === 'approved' ? 'badge-ok' : a.status === 'denied' ? 'badge-danger' : a.status === 'expired' ? 'badge-warn' : 'badge-neutral'}`}>{a.status}</span></td>
                  <td>{a.effective_date || '—'}</td>
                  <td>{a.expiry_date || '—'}</td>
                  {canManage && <td><button type="button" className="link" onClick={() => setAuthForm({ id: a.id, program_type: a.program_type, payer: a.payer || '', auth_number: a.auth_number || '', status: a.status, effective_date: a.effective_date || '', expiry_date: a.expiry_date || '', notes: a.notes || '' })}>Edit</button></td>}
                </tr>
              ))}</tbody>
            </table></div>
          )}
          {canManage && (
            <form className="php-escalation-form" onSubmit={saveAuth}>
              <div className="form-grid">
                <label>Program<select value={authForm.program_type} onChange={setAuth('program_type')}><option value="php">PHP</option><option value="iop">IOP</option></select></label>
                <label>Status<select value={authForm.status} onChange={setAuth('status')}><option value="pending">Pending</option><option value="approved">Approved</option><option value="denied">Denied</option><option value="expired">Expired</option></select></label>
                <label>Payer<input value={authForm.payer} onChange={setAuth('payer')} maxLength={200} placeholder="e.g. Medicare"/></label>
                <label>Auth #<input value={authForm.auth_number} onChange={setAuth('auth_number')} maxLength={100}/></label>
                <label>Effective date<input type="date" value={authForm.effective_date} onChange={setAuth('effective_date')}/></label>
                <label>Expiry date<input type="date" value={authForm.expiry_date} onChange={setAuth('expiry_date')}/></label>
                <label className="span2">Notes<textarea rows={2} value={authForm.notes} onChange={setAuth('notes')} maxLength={5000}/></label>
              </div>
              <button className="button secondary" disabled={busy}>{authForm.id ? 'Update authorization' : 'Add authorization'}</button>
            </form>
          )}
        </section>

        <HistoryPanel payload={payload} />
      </section>
    </>
  );
}

function TriSelect({ label, value, onChange }) {
  return (
    <label>{label}
      <select value={value} onChange={onChange}>
        <option value="">— Unset (missing) —</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    </label>
  );
}

function ResultPanel({ result, patient }) {
  const meta = STATUS[result.status] || { label: result.status, tone: 'pending' };
  const resultDx = result.diagnosis_code ? (patient.diagnoses || []).find(d => d.code === result.diagnosis_code) : null;
  return (
    <div className="php-result">
      <div className={`php-result-head ${meta.tone}`}>
        <span>{result.status === 'ELIGIBLE' ? <ShieldCheck size={18} /> : <ShieldAlert size={18} />}</span>
        <strong>{meta.label}</strong>
        {result.diagnosis_code && <small>ICD-10 {result.diagnosis_code}</small>}
        {resultDx && <DiagnosisClassBadge diagnosis={resultDx} />}
      </div>
      <div className="php-checks">
        {Object.values(result.checks).map(c => {
          const Icon = STATE_ICON[c.state];
          return (
            <div key={c.label} className={`php-check ${c.state.toLowerCase()}`}>
              <Icon size={16} />
              <div><strong>{c.label}</strong>{c.detail && <p>{c.detail}</p>}</div>
              <span className="badge">{c.state}</span>
            </div>
          );
        })}
      </div>
      {result.reasons.length > 0 && (
        <div className="php-reasons">
          <strong>Reasons</strong>
          <ul>{result.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

// Read-only reference: the latest PHQ-9 is surfaced for clinician context. It NEVER feeds
// phpEvaluate()/evaluatePhpEligibility() — eligibility stays exclusively engine-driven.
function ClinicalScreenings({ patient, notify, onViewAll }) {
  const [screenings, setScreenings] = useState(DEMO ? (patient.phq9 || []) : []);

  useEffect(() => {
    if (DEMO) { setScreenings(patient.phq9 || []); return; }
    api(`/patients/${patient.id}/phq9`).then(d => setScreenings(d.screenings || [])).catch(e => notify(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient.id]);

  const latest = screenings[0];
  return (
    <section className="section-space">
      <div className="content-heading">
        <div><h4>Clinical screenings</h4><p>Latest PHQ-9 for reference. Screening scores never determine PHP eligibility, medical necessity, or certification.</p></div>
        {onViewAll && <button type="button" className="link" onClick={onViewAll}><ClipboardList size={14} /> View full screening</button>}
      </div>
      {!latest && <p className="empty-copy">No PHQ-9 screening on record.</p>}
      {latest && (
        <div className="php-screening">
          <div className="php-screening-score"><strong>{latest.total}</strong><span>/ 27</span></div>
          <div className="php-screening-meta">
            <b>{PHQ9_SEVERITY_LABEL[latest.severity] || latest.severity}</b>
            <small>Completed {new Date(latest.completed_at).toLocaleString()}{latest.completed_by_name ? ' · ' + latest.completed_by_name : ''}</small>
            {latest.functional_impairment && <small>Functional impact: {latest.functional_impairment}</small>}
          </div>
        </div>
      )}
    </section>
  );
}

function HistoryPanel({ payload }) {
  if (!payload) return null;
  const assessments = payload.assessments || [];
  const certifications = payload.certifications || [];
  return (
    <div className="php-history section-space">
      <div className="content-heading"><div><h4>Decision &amp; certification history</h4><p>Append-only. Every evaluation and certification is retained as a new versioned record.</p></div></div>
      {assessments.length === 0 && certifications.length === 0 && <p className="empty-copy">No eligibility decisions or certifications recorded yet.</p>}
      {assessments.length > 0 && (
        <div className="table-scroll"><table>
          <thead><tr><th>When</th><th>Program</th><th>Decision</th><th>Rule</th><th>Evaluator</th></tr></thead>
          <tbody>{assessments.map(a => (
            <tr key={a.id}>
              <td>{a.created_at}</td>
              <td><span className="badge neutral">{a.program_type === 'iop' ? 'IOP' : 'PHP'}</span></td>
              <td><span className={`php-status-badge ${STATUS[a.status]?.tone || 'pending'}`}>{STATUS[a.status]?.label || a.status}</span></td>
              <td>{a.rule_version}</td>
              <td>{a.evaluator_role || '—'}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
      {certifications.length > 0 && (
        <div className="table-scroll"><table>
          <thead><tr><th>Signed</th><th>Type</th><th>Physician</th><th>NPI</th><th>Recert due</th><th>Snapshot</th></tr></thead>
          <tbody>{certifications.map(c => (
            <tr key={c.id}>
              <td>{c.signed_at || c.created_at}</td>
              <td><span className="badge neutral">{c.type}</span></td>
              <td>{c.physician_name || '—'}</td>
              <td>{c.npi || '—'}</td>
              <td>{c.recert_due_date || '—'}</td>
              <td>{c.pdf_storage_key ? <a href={`/api/php/certifications/${c.id}/pdf`} target="_blank" rel="noopener noreferrer"><FileText size={14} /> PDF</a> : '—'}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
    </div>
  );
}
